// src/pages/LandingPage.tsx
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useTranslation } from "../hooks/useTranslation";
import PublicHeader from "../components/PublicHeader";
import VideoModal from "../components/VideoModal";
import { C, FONT } from "../theme";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// ── Animated counter hook ─────────────────────────────────────────────────────
function useCounter(target: number, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);
const YouTubeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);
const ChevronDown = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const PlayCircle = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
  </svg>
);

// ── Stat card ─────────────────────────────────────────────────────────────────
interface StatCardProps {
  value: number;
  suffix?: string;
  label: string;
  label2?: string;
  started: boolean;
  isMobile: boolean;
}
function StatCard({ value, suffix = "+", label, label2, started, isMobile }: StatCardProps) {
  const count = useCounter(value, 1800, started);
  return (
    <div style={{
      ...s.statCard,
      padding: isMobile ? "12px 16px" : "16px 24px",
    }}>
      <div style={s.statValue}>
        {count}{suffix}
      </div>
      <div style={s.statLabel}>{label}</div>
      {label2 && <div style={s.statLabel2}>{label2}</div>}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const { currentUser, userRole, loading, login, resetPassword } = useAuth();
  const { resolvedTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const w = useWindowWidth();
  const isMobile = w < 768;
  const isTablet = w >= 768 && w < 1100;
  const darkTheme = resolvedTheme === "dark";

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [cookieAccepted, setCookieAccepted] = useState(true);
  const [statsStarted, setStatsStarted] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [loginError, setLoginError] = useState<string>("");
  const [loginLoading, setLoginLoading] = useState<boolean>(false);
  const [focusedField, setFocusedField] = useState<string>("");
  const [authView, setAuthView] = useState<"login" | "forgot">("login");
  const [resetMessage, setResetMessage] = useState<string>("");
  const [resetLoading, setResetLoading] = useState<boolean>(false);

  const statsRef = useRef<HTMLDivElement>(null);

  // Hero entrance
  useEffect(() => {
    const t = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Keep both official hero assets warm so switching theme never briefly shows
  // the previous theme's artwork while the other file is being fetched.
  useEffect(() => {
    ["/background-clean.png", "/background-clean1.png"].forEach((src) => {
      const image = new Image();
      image.src = src;
    });
  }, []);

  // Cookie consent
  useEffect(() => {
    const accepted = localStorage.getItem("cookieConsent");
    setCookieAccepted(!!accepted);
  }, []);

  // Auto-open modal from /login route
  useEffect(() => {
    if (location.pathname === "/login") setShowLoginModal(true);
  }, [location.pathname]);

  // `/` remains a real public page for authenticated users. Only the explicit
  // `/login` alias redirects an existing session back to its workspace.
  useEffect(() => {
    if (!loading && currentUser && location.pathname === "/login") {
      navigate(userRole === "coach" ? "/dashboard" : "/admin", { replace: true });
    }
  }, [loading, currentUser, userRole, navigate, location.pathname]);

  // Stats intersection observer
  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsStarted(true); },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const role = await login(email, password);
      setShowLoginModal(false);
      if (role === "admin") navigate("/admin");
      else navigate("/dashboard");
    } catch (err: any) {
      switch (err.code) {
        case "auth/user-not-found":
        case "auth/wrong-password":
        case "auth/invalid-credential":
          setLoginError(t("login.error.invalid"));
          break;
        default:
          setLoginError(t("login.error.default"));
      }
    } finally {
      setLoginLoading(false);
    }
  };

  // Email-format check run before firing the reset request, so an obviously
  // malformed address gets immediate feedback (and never hits Firebase).
  const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  // Switches between the sign-in and forgot-password views, clearing any stale
  // error / success text so the two flows never bleed into each other.
  const switchAuthView = (view: "login" | "forgot") => {
    setAuthView(view);
    setLoginError("");
    setResetMessage("");
  };

  const closeLoginModal = () => {
    setShowLoginModal(false);
    switchAuthView("login");
  };

  // Forgot-password submit. Always ends on the SAME neutral message regardless
  // of whether the email is registered — we never disclose account existence.
  // Genuine errors (network, rate-limit) are only logged, not surfaced.
  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setResetMessage("");
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setLoginError(t("login.reset.invalidEmail"));
      return;
    }
    setResetLoading(true);
    try {
      await resetPassword(trimmed);
    } catch (err: any) {
      console.warn("Password reset request issue:", err?.code ?? err);
    } finally {
      setResetLoading(false);
      setResetMessage(t("login.reset.sent"));
    }
  };

  const acceptCookies = () => {
    localStorage.setItem("cookieConsent", "true");
    setCookieAccepted(true);
  };

  const scrollToStats = () => {
    statsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
    <Helmet>
      <title>{t("seo.landing.title")}</title>
      <meta name="description" content={t("seo.landing.description")} />
      <link rel="canonical" href="https://registration.taekwondo.ge/" />
      <meta property="og:title" content={t("seo.landing.title")} />
      <meta property="og:description" content={t("seo.landing.description")} />
      <meta property="og:url" content="https://registration.taekwondo.ge/" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://registration.taekwondo.ge/geo.png" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={t("seo.landing.title")} />
      <meta name="twitter:description" content={t("seo.landing.description")} />
      <meta name="twitter:image" content="https://registration.taekwondo.ge/geo.png" />
      <link rel="alternate" hrefLang="en" href="https://registration.taekwondo.ge/" />
      <link rel="alternate" hrefLang="ka" href="https://registration.taekwondo.ge/" />
      <link rel="alternate" hrefLang="x-default" href="https://registration.taekwondo.ge/" />
    </Helmet>
      {/* ── CSS Keyframes injected once ───────────────────────────────────── */}
      <style>{`
        @keyframes heroBgZoom {
          from { transform: scale(1.08); }
          to   { transform: scale(1); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-32px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes pulseRed {
          0%, 100% { box-shadow: 0 0 0 0 rgba(218,41,28,0.35); }
          50%       { box-shadow: 0 0 0 10px rgba(218,41,28,0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes verticalScroll {
          0%   { transform: translateY(0); }
          100% { transform: translateY(-6px); }
        }
        .hero-loaded .anim-0 { animation: fadeUp 0.7s cubic-bezier(.22,1,.36,1) 0.1s both; }
        .hero-loaded .anim-1 { animation: fadeUp 0.7s cubic-bezier(.22,1,.36,1) 0.2s both; }
        .hero-loaded .anim-2 { animation: fadeUp 0.7s cubic-bezier(.22,1,.36,1) 0.35s both; }
        .hero-loaded .anim-3 { animation: fadeUp 0.7s cubic-bezier(.22,1,.36,1) 0.5s both; }
        .hero-loaded .anim-4 { animation: fadeUp 0.7s cubic-bezier(.22,1,.36,1) 0.65s both; }
        .hero-loaded .anim-5 { animation: fadeUp 0.7s cubic-bezier(.22,1,.36,1) 0.8s both; }
        .hero-loaded .anim-nav { animation: fadeIn 0.6s ease 0.05s both; }
        .hero-loaded .anim-side { animation: slideInRight 0.7s cubic-bezier(.22,1,.36,1) 0.9s both; }
        .landing-hero { min-height: 100vh; min-height: 100svh; }
        .landing-hero__content {
          min-height: 100vh;
          min-height: 100svh;
          justify-content: center !important;
          padding-top: clamp(88px, 12vh, 124px) !important;
          padding-bottom: clamp(54px, 7vh, 86px) !important;
        }
        .landing-hero__title {
          font-size: clamp(64px, min(8.5vw, 10vh), 118px);
        }
        @media (max-width: 1099px) {
          .landing-hero__title { font-size: clamp(56px, min(9vw, 9.5vh), 92px); }
        }
        @media (max-width: 767px) {
          .landing-hero__content {
            padding-top: 78px !important;
            padding-bottom: 64px !important;
          }
          .landing-hero__title { font-size: clamp(46px, 13vw, 68px); }
        }
        .cta-primary:hover { opacity: 0.88 !important; transform: translateY(-1px) !important; }
        .cta-secondary:hover { background: var(--color-surface-3) !important; }
        .social-btn:hover { background: var(--color-surface-3) !important; transform: translateY(-2px); }
        .nav-link:hover { color: var(--color-accent) !important; }
        .login-btn-nav:hover { background: rgba(218,41,28,0.85) !important; }
        .stat-card-hover:hover { background: var(--color-surface-3) !important; }
        .scroll-indicator { animation: verticalScroll 1.2s ease-in-out infinite alternate; }
      `}</style>

      <div style={s.root}>

        {/* ════════════ HERO SECTION ════════════════════════════════════════ */}
        <section
          className={`landing-hero${heroLoaded ? " hero-loaded" : ""}`}
          style={s.hero}
        >
          {/* Background image with zoom animation */}
          <div style={s.heroBgWrap}>
            <div style={{
              ...s.heroBg,
              backgroundImage: `url('${darkTheme ? "/background-clean.png" : "/background-clean1.png"}')`,
              animation: heroLoaded ? "heroBgZoom 1.8s cubic-bezier(.22,1,.36,1) forwards" : "none",
            }} />
            {/* Multi-layer overlay for text legibility */}
            <div style={{ ...s.heroOverlay1, background: darkTheme
              ? "linear-gradient(105deg, rgba(5,5,10,0.92) 0%, rgba(5,5,10,0.75) 55%, rgba(5,5,10,0.25) 100%)"
              : "linear-gradient(105deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.78) 55%, rgba(255,255,255,0.12) 100%)" }} />
            <div style={{ ...s.heroOverlay2, background: darkTheme
              ? "linear-gradient(to top, rgba(5,5,10,0.98) 0%, rgba(5,5,10,0.3) 40%, transparent 70%)"
              : "linear-gradient(to top, var(--color-bg) 0%, rgba(255,255,255,0.18) 42%, transparent 72%)" }} />
            <div style={{ ...s.heroOverlayVignette, background: darkTheme
              ? "radial-gradient(ellipse at 30% 50%, transparent 40%, rgba(0,0,0,0.55) 100%)"
              : "radial-gradient(ellipse at 30% 50%, transparent 48%, rgba(255,255,255,0.22) 100%)" }} />
          </div>

          {/* ── Top Navigation ─────────────────────────────────────────── */}
          <PublicHeader overlay onLogin={() => setShowLoginModal(true)} />

          {/* ── Hero Content ───────────────────────────────────────────── */}
          <div style={{
            ...s.heroContent,
            paddingLeft: isMobile ? 24 : isTablet ? 48 : 80,
            paddingRight: isMobile ? 24 : isTablet ? 48 : 80,
            alignItems: isMobile ? "center" : "flex-start",
            textAlign: isMobile ? "center" : "left",
          }} className="landing-hero__content">



            {/* Tagline */}
            <div className="anim-0" style={s.tagline}>
              {t("landing.heroTagline")}
            </div>

            {/* Main title */}
            <div className="anim-1" style={{
              ...s.heroTitleBlock,
              maxWidth: isMobile ? "100%" : "680px",
            }}>
              <h1 className="landing-hero__title" style={{ ...s.heroTitleWhite, color: darkTheme ? C.heroText : C.text, textShadow: darkTheme ? s.heroTitleWhite.textShadow : "0 3px 24px rgba(15,23,42,0.10)" }}>
                GEORGIAN<br />TAEKWONDO
              </h1>
              <h1 className="landing-hero__title" style={s.heroTitleRed}>
                NATIONAL<br />FEDERATION
              </h1>
            </div>

            {/* Georgian name */}
            <div className="anim-2" style={{
              ...s.georgianName,
              color: darkTheme ? C.heroText2 : C.text2,
              fontSize: isMobile ? "13px" : "15px",
            }}>
              საქართველოს ტაეკვონდოს ეროვნული ფედერაცია
            </div>

            {/* Subtitle */}
            <p className="anim-3" style={{
              ...s.heroSubtitle,
              color: darkTheme ? C.heroText2 : C.text2,
              fontSize: isMobile ? "14px" : "16px",
              maxWidth: isMobile ? "100%" : "440px",
            }}>
              {t("landing.heroSubtitle1")}<br />
              {t("landing.heroSubtitle2")}
            </p>

            {/* CTA Buttons */}
            <div className="anim-4" style={{
              ...s.ctaRow,
              justifyContent: isMobile ? "center" : "flex-start",
              flexWrap: "wrap",
            }}>
              <button
                className="cta-primary"
                style={s.ctaPrimary}
                onClick={scrollToStats}
              >
                {t("landing.discoverMore")} &nbsp;→
              </button>
              <button
                className="cta-secondary"
                style={s.ctaSecondary}
                onClick={() => setShowVideoModal(true)}
              >
                <PlayCircle />
                <span>{t("landing.watchVideo")}</span>
              </button>
            </div>

            {/* Social row */}
            <div className="anim-5" style={{
              ...s.socialRow,
              justifyContent: isMobile ? "center" : "flex-start",
            }}>
              <a
                href="https://www.facebook.com/georgiantaekwondo/"
                target="_blank"
                rel="noreferrer"
                className="social-btn"
                style={s.socialBtn}
                title="Facebook"
              >
                <FacebookIcon />
              </a>
              <a
                href="https://www.youtube.com/channel/UC8oy7IbaKKirrHWAPG1aolg"
                target="_blank"
                rel="noreferrer"
                className="social-btn"
                style={s.socialBtn}
                title="YouTube"
              >
                <YouTubeIcon />
              </a>
              <a
                href="https://taekwondo.ge"
                target="_blank"
                rel="noreferrer"
                className="social-btn"
                style={{ ...s.socialBtn, fontSize: "11px", fontWeight: 700, color: C.text, letterSpacing: "0.5px" }}
              >
                taekwondo.ge
              </a>
            </div>
          </div>

          {/* ── Right-edge vertical label — desktop only ───────────────── */}
          {!isMobile && (
            <div className="anim-side" style={s.verticalLabel}>
              <span style={s.verticalLabelText}>
                {t("landing.verticalLabel")}
              </span>
            </div>
          )}

          {/* ── Scroll indicator ────────────────────────────────────────── */}
          <div className="scroll-indicator" style={{ ...s.scrollIndicator, color: darkTheme ? C.heroText2 : C.text2 }} onClick={scrollToStats}>
            <ChevronDown />
          </div>
        </section>

        {/* ════════════ STATS BAR ══════════════════════════════════════════ */}
        <div ref={statsRef} style={{
          ...s.statsBar,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? "1px" : "1px",
        }}>
          <StatCard value={500} suffix="+" label={t("landing.stats.registeredAthletes")} started={statsStarted} isMobile={isMobile} />
          <StatCard value={100} suffix="+" label={t("landing.stats.nationalCompetitions")} started={statsStarted} isMobile={isMobile} />
          <StatCard value={50}  suffix="+" label={t("landing.stats.internationalMedals")} started={statsStarted} isMobile={isMobile} />
          <StatCard value={1}   suffix=""  label={t("landing.stats.nation")} label2={t("landing.stats.nationTagline")} started={statsStarted} isMobile={isMobile} />
        </div>

        {/* ════════════ EVENTS & RANKINGS PREVIEW ═════════════════════════ */}
        <section style={{
          ...s.previewSection,
          padding: isMobile ? "64px 20px" : "80px 64px",
        }}>
          <div style={s.previewGrid}>
            {/* Events card */}
            <div style={s.previewCard} onClick={() => navigate("/events")}>
              <div style={{ ...s.previewCardAccent, background: C.red }} />
              <div style={s.previewCardIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <div style={s.previewCardTitle}>{t("landing.events")}</div>
              <p style={s.previewCardSub}>{t("landing.eventsCard.description")}</p>
              <div style={s.previewCardCTA}>{t("landing.eventsCard.cta")}</div>
            </div>

            {/* Rankings card */}
            <div style={s.previewCard} onClick={() => navigate("/rankings")}>
              <div style={{ ...s.previewCardAccent, background: C.blue }} />
              <div style={s.previewCardIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              </div>
              <div style={s.previewCardTitle}>{t("landing.rankings")}</div>
              <p style={s.previewCardSub}>{t("landing.rankingsCard.description")}</p>
              <div style={{ ...s.previewCardCTA, color: C.blue }}>{t("landing.rankingsCard.cta")}</div>
            </div>

            {/* Login/portal card */}
            <div style={{ ...s.previewCard, background: C.surface, border: `1px solid ${C.red}` }}
              onClick={() => currentUser ? navigate(userRole === "coach" ? "/dashboard" : "/admin") : setShowLoginModal(true)}>
              <div style={{ ...s.previewCardAccent, background: C.red, opacity: 0.6 }} />
              <div style={s.previewCardIcon}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                  <polyline points="10 17 15 12 10 7" />
                  <line x1="15" y1="12" x2="3" y2="12" />
                </svg>
              </div>
              <div style={{ ...s.previewCardTitle, color: C.text }}>{currentUser ? t("nav.workspace") : t("landing.coachPortal.title")}</div>
              <p style={{ ...s.previewCardSub, color: C.text2 }}>{t("landing.coachPortal.description")}</p>
              <div style={{ ...s.previewCardCTA, color: C.red }}>{currentUser ? t("nav.workspace") : t("landing.coachPortal.cta")}</div>
            </div>
          </div>
          <div style={{ maxWidth: 1120, margin: "54px auto 18px" }}>
            <div style={{ ...s.heroEyebrow, color: C.red }}>{t("publicDirectory.officialRegistry")}</div>
            <h2 style={{ margin: "7px 0 0", color: C.text, fontFamily: FONT.display, fontSize: isMobile ? 32 : 42, fontWeight: 400, letterSpacing: "1px" }}>{t("publicDirectory.discoveryTitle")}</h2>
          </div>
          <div style={{ ...s.previewGrid, gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))" }}>
            <div style={s.previewCard} onClick={() => navigate("/athletes")}>
              <div style={{ ...s.previewCardAccent, background: C.red }} />
              <div style={s.previewCardIcon}><span style={{ color: C.red, fontSize: 24 }}>◎</span></div>
              <div style={s.previewCardTitle}>{t("publicDirectory.athletes.title")}</div>
              <p style={s.previewCardSub}>{t("publicDirectory.athletes.homeDescription")}</p>
              <div style={s.previewCardCTA}>{t("publicDirectory.athletes.browse")} →</div>
            </div>
            <div style={s.previewCard} onClick={() => navigate("/clubs")}>
              <div style={{ ...s.previewCardAccent, background: C.blue }} />
              <div style={s.previewCardIcon}><span style={{ color: C.blue, fontSize: 24 }}>◇</span></div>
              <div style={s.previewCardTitle}>{t("publicDirectory.clubs.title")}</div>
              <p style={s.previewCardSub}>{t("publicDirectory.clubs.homeDescription")}</p>
              <div style={{ ...s.previewCardCTA, color: C.blue }}>{t("publicDirectory.clubs.browse")} →</div>
            </div>
          </div>
        </section>

        {/* ════════════ FOOTER STRIP ═══════════════════════════════════════ */}
        <footer style={{
          ...s.footerStrip,
          flexDirection: isMobile ? "column" : "row",
          textAlign: isMobile ? "center" : "left",
          gap: isMobile ? "16px" : "32px",
          padding: isMobile ? "28px 24px" : "24px 64px",
        }}>
          <div style={s.footerContact}>
            <span>📍 თბილისი, ხიზაბავრის #1, ჩიხი #5</span>
            <span style={{ margin: "0 8px", opacity: 0.3 }}>|</span>
            <span>📞 +995 551 071 421</span>
            <span style={{ margin: "0 8px", opacity: 0.3 }}>|</span>
            <span>✉️ taekwondogeo@gmail.com</span>
          </div>
          <div style={s.footerLinks}>
            <button style={s.footerLink} onClick={() => navigate("/privacy")}>
              {t("landing.privacy")}
            </button>
            <a href="https://taekwondo.ge" target="_blank" rel="noreferrer" style={s.footerLink}>
              taekwondo.ge
            </a>
          </div>
        </footer>

      </div>

      {/* ════════════ LOGIN MODAL ════════════════════════════════════════════ */}
      {showLoginModal && (
        <div style={s.modalOverlay} onClick={closeLoginModal}>
          <div style={s.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalAccentBar} />
            <div style={s.modalHeader}>
              <div style={s.modalLogoRow}>
                <img src="/geo.png" alt="Logo" style={{ height: 36, objectFit: "contain" }} />
                <div>
                  <div style={s.modalTitle}>{t("landing.loginTitle")}</div>
                  <div style={s.modalSub}>{t("landing.loginSub")}</div>
                </div>
              </div>
              <button style={s.closeBtn} onClick={closeLoginModal}>✕</button>
            </div>

            {loginError && (
              <div style={s.errorBox}>
                <span style={{ color: C.red, fontSize: 8 }}>●</span> {loginError}
              </div>
            )}

            {resetMessage && (
              <div style={{
                margin: "0 24px 16px",
                backgroundColor: "rgba(34,197,94,0.10)",
                border: "1px solid rgba(34,197,94,0.35)",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
                lineHeight: 1.5,
                color: "var(--color-success)",
              }}>
                {resetMessage}
              </div>
            )}

            {authView === "login" ? (
              <form onSubmit={handleLogin}>
                <div style={s.fieldGroup}>
                  <label style={s.label}>{t("login.email")}</label>
                  <input
                    style={{ ...s.input, ...(focusedField === "email" ? s.inputFocus : {}) }}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField("email")}
                    onBlur={() => setFocusedField("")}
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                  />
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.label}>{t("login.password")}</label>
                  <input
                    style={{ ...s.input, ...(focusedField === "password" ? s.inputFocus : {}) }}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField("password")}
                    onBlur={() => setFocusedField("")}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                  />
                </div>
                <div style={{ padding: "0 24px", display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                  <button
                    type="button"
                    onClick={() => switchAuthView("forgot")}
                    style={{ background: "none", border: "none", color: C.red, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}
                  >
                    {t("login.forgotLink")}
                  </button>
                </div>
                <div style={{ padding: "0 24px 24px" }}>
                  <button style={s.loginSubmitBtn} type="submit" disabled={loginLoading}>
                    {loginLoading ? (
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <span style={s.spinner} /> {t("login.loading")}
                      </span>
                    ) : t("login.button")}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleResetRequest}>
                <div style={{ padding: "0 24px 12px", fontSize: 13, lineHeight: 1.5, color: C.text2 }}>
                  {t("login.reset.subtitle")}
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.label}>{t("login.email")}</label>
                  <input
                    style={{ ...s.input, ...(focusedField === "resetEmail" ? s.inputFocus : {}) }}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField("resetEmail")}
                    onBlur={() => setFocusedField("")}
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                  />
                </div>
                <div style={{ padding: "4px 24px 10px" }}>
                  <button style={s.loginSubmitBtn} type="submit" disabled={resetLoading}>
                    {resetLoading ? (
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <span style={s.spinner} /> {t("login.reset.sending")}
                      </span>
                    ) : t("login.reset.button")}
                  </button>
                </div>
                <div style={{ padding: "0 24px 24px", textAlign: "center" }}>
                  <button
                    type="button"
                    onClick={() => switchAuthView("login")}
                    style={{ background: "none", border: "none", color: C.text2, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}
                  >
                    {t("login.reset.back")}
                  </button>
                </div>
              </form>
            )}
            <div style={s.modalFooter}>{t("login.footer")}</div>
          </div>
        </div>
      )}

      {/* ════════════ VIDEO MODAL ════════════════════════════════════════════ */}
      {/* Two tutorials streamed from YouTube (embedded). No local files / no
          hosting bandwidth — see VideoModal.tsx for the embed handling. */}
      {showVideoModal && (
        <VideoModal
          title={t("landing.tutorial")}
          videos={[
            { src: "https://www.youtube.com/watch?v=itf-5PaRAcg", label: t("landing.video1") },
            { src: "https://www.youtube.com/watch?v=OJSe0Ybpfms", label: t("landing.video2") },
          ]}
          unavailableLabel={t("landing.videoUnavailable")}
          onClose={() => setShowVideoModal(false)}
        />
      )}

      {/* ════════════ COOKIE BAR ═════════════════════════════════════════════ */}
      {!cookieAccepted && (
        <div style={s.cookieBar}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
            <span style={{ fontSize: 20 }}>🍪</span>
            <span style={s.cookieText}>
              {t("landing.cookie.text")} &nbsp;
              <button style={s.cookieLearnBtn} onClick={() => navigate("/privacy")}>
                {t("landing.cookie.learnMore")}
              </button>
            </span>
          </div>
          <button style={s.cookieAcceptBtn} onClick={acceptCookies}>
            {t("landing.cookie.accept")}
          </button>
        </div>
      )}
    </>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {

  root: {
    minHeight: "100vh",
    backgroundColor: C.bg,
    fontFamily: FONT.body,
  },

  // ── Hero
  hero: {
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  heroBgWrap: {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
  },
  heroBg: {
    position: "absolute",
    inset: "-4%",
    backgroundSize: "cover",
    backgroundPosition: "center top",
    backgroundRepeat: "no-repeat",
  },
  heroOverlay1: {
    position: "absolute",
    inset: 0,
  },
  heroOverlay2: {
    position: "absolute",
    inset: 0,
  },
  heroOverlayVignette: {
    position: "absolute",
    inset: 0,
  },

  // ── Nav
  nav: {
    position: "relative",
    zIndex: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  navLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  navLogo: {
    height: 44,
    width: "auto",
    objectFit: "contain",
    filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.5))",
  },
  navBrand: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },
  navBrandTop: {
    fontFamily: FONT.display,
    fontSize: 16,
    letterSpacing: "2px",
    color: C.text,
    lineHeight: 1,
  },
  navBrandBot: {
    fontSize: 9,
    color: C.text2,
    letterSpacing: "1.5px",
    textTransform: "uppercase" as const,
  },
  navLinks: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  navLink: {
    background: "none",
    border: "none",
    color: C.text,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.5px",
    cursor: "pointer",
    padding: "8px 16px",
    borderRadius: 6,
    transition: "color 0.15s",
    fontFamily: FONT.body,
    textTransform: "uppercase" as const,
  },
  navRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  navLoginBtn: {
    padding: "9px 20px",
    backgroundColor: C.red,
    color: C.onAccent,
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
    fontFamily: FONT.body,
    transition: "background 0.15s",
    whiteSpace: "nowrap" as const,
    animation: "pulseRed 2.4s ease-in-out 2s infinite",
  },

  // ── Hero content
  heroContent: {
    position: "relative",
    zIndex: 10,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  verticalSteps: {
    position: "absolute",
    left: 20,
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    alignItems: "center",
  },
  stepItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    transition: "opacity 0.2s",
  },
  stepDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
  },
  stepNum: {
    fontSize: 10,
    fontWeight: 700,
    color: C.text2,
    letterSpacing: "1px",
    writingMode: "vertical-rl" as const,
  },
  tagline: {
    fontFamily: FONT.body,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "3px",
    color: C.red,
    textTransform: "uppercase" as const,
    marginBottom: 16,
  },
  heroTitleBlock: {
    lineHeight: 0.9,
    marginBottom: 8,
  },
  heroTitleWhite: {
    fontFamily: FONT.display,
    color: C.heroText,
    margin: 0,
    padding: 0,
    letterSpacing: "2px",
    lineHeight: 0.92,
    fontWeight: 400,
    textShadow: "0 4px 32px rgba(0,0,0,0.6)",
  },
  heroTitleRed: {
    fontFamily: FONT.display,
    color: C.red,
    margin: 0,
    padding: 0,
    letterSpacing: "2px",
    lineHeight: 0.92,
    fontWeight: 400,
    fontStyle: "italic",
    textShadow: "0 4px 32px rgba(218,41,28,0.35)",
  },
  georgianName: {
    color: C.heroText2,
    fontFamily: FONT.body,
    fontWeight: 400,
    letterSpacing: "0.5px",
    marginTop: 12,
    marginBottom: 20,
  },
  heroSubtitle: {
    color: C.heroText2,
    fontFamily: FONT.body,
    fontWeight: 400,
    lineHeight: 1.65,
    margin: "0 0 28px 0",
  },

  // CTA
  ctaRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 28,
  },
  ctaPrimary: {
    padding: "14px 32px",
    backgroundColor: C.red,
    color: C.onAccent,
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: "1.5px",
    cursor: "pointer",
    fontFamily: FONT.body,
    transition: "opacity 0.15s, transform 0.15s",
  },
  ctaSecondary: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "13px 24px",
    backgroundColor: C.surface2,
    color: C.text,
    border: `1px solid ${C.border2}`,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "1px",
    cursor: "pointer",
    fontFamily: FONT.body,
    transition: "background 0.15s",
    backdropFilter: "blur(6px)",
  },

  // Social
  socialRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  socialBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "9px 14px",
    backgroundColor: C.surface2,
    color: C.text,
    border: `1px solid ${C.border2}`,
    borderRadius: 8,
    cursor: "pointer",
    transition: "background 0.15s, transform 0.15s",
    textDecoration: "none",
    fontFamily: FONT.body,
    backdropFilter: "blur(6px)",
    gap: 6,
  },

  // Vertical label
  verticalLabel: {
    position: "absolute",
    right: 20,
    top: "50%",
    transform: "translateY(-50%) rotate(90deg)",
    transformOrigin: "center center",
    zIndex: 10,
  },
  verticalLabelText: {
    fontFamily: FONT.body,
    fontSize: 9,
    fontWeight: 700,
    color: C.text3,
    letterSpacing: "3px",
    textTransform: "uppercase" as const,
    whiteSpace: "nowrap" as const,
  },

  // Scroll indicator
  scrollIndicator: {
    position: "absolute",
    bottom: 28,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 10,
    color: C.heroText2,
    cursor: "pointer",
  },

  // ── Stats bar
  statsBar: {
    display: "flex",
    backgroundColor: C.surface,
    borderTop: `1px solid ${C.border}`,
  },
  statCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    borderRight: `1px solid ${C.border}`,
    cursor: "default",
    transition: "background 0.2s",
  },
  statValue: {
    fontFamily: FONT.display,
    fontSize: "clamp(36px, 5vw, 56px)",
    color: C.red,
    lineHeight: 1,
    letterSpacing: "1px",
  },
  statLabel: {
    fontFamily: FONT.body,
    fontSize: 10,
    fontWeight: 700,
    color: C.text2,
    letterSpacing: "2px",
    textTransform: "uppercase" as const,
  },
  statLabel2: {
    fontFamily: FONT.body,
    fontSize: 9,
    color: C.text3,
    letterSpacing: "1.5px",
    textTransform: "uppercase" as const,
    marginTop: 2,
  },

  // ── Preview section
  previewSection: {
    backgroundColor: C.surface2,
  },
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 20,
    maxWidth: 1100,
    margin: "0 auto",
  },
  previewCard: {
    backgroundColor: C.surface,
    borderRadius: 16,
    padding: "32px 28px",
    border: `1px solid ${C.border}`,
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    position: "relative",
    overflow: "hidden",
  },
  previewCardAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    borderRadius: "16px 16px 0 0",
  },
  previewCardIcon: {
    marginBottom: 16,
    width: 48,
    height: 48,
    backgroundColor: C.surface2,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  previewCardTitle: {
    fontFamily: FONT.display,
    fontSize: 28,
    letterSpacing: "1.5px",
    color: C.text,
    marginBottom: 10,
    lineHeight: 1,
  },
  previewCardSub: {
    fontSize: 13,
    color: C.text2,
    lineHeight: 1.6,
    margin: "0 0 20px",
    fontFamily: FONT.body,
  },
  previewCardCTA: {
    fontSize: 12,
    fontWeight: 800,
    color: C.red,
    letterSpacing: "1.5px",
    fontFamily: FONT.body,
  },

  // ── Footer strip
  footerStrip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: C.bg,
    borderTop: `1px solid ${C.border}`,
    flexWrap: "wrap" as const,
  },
  footerContact: {
    fontSize: 12,
    color: C.text3,
    fontFamily: FONT.body,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap" as const,
    gap: 4,
    lineHeight: 1.6,
  },
  footerLinks: {
    display: "flex",
    alignItems: "center",
    gap: 20,
  },
  footerLink: {
    fontSize: 11,
    color: C.text3,
    background: "none",
    border: "none",
    cursor: "pointer",
    textDecoration: "none",
    fontFamily: FONT.body,
    letterSpacing: "0.5px",
  } as React.CSSProperties,

  // ── Login modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: 20,
    backdropFilter: "blur(4px)",
  },
  modalCard: {
    backgroundColor: C.surface,
    borderRadius: 18,
    width: "100%",
    maxWidth: 400,
    overflow: "hidden",
    boxShadow: "0 32px 80px rgba(0,0,0,0.4)",
  },
  modalAccentBar: {
    height: 4,
    background: C.red,
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "20px 24px 14px",
  },
  modalLogoRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  modalTitle: {
    fontFamily: FONT.display,
    fontSize: 24,
    letterSpacing: "1px",
    color: C.text,
    lineHeight: 1,
  },
  modalSub: {
    fontSize: 12,
    color: C.text2,
    marginTop: 4,
    fontFamily: FONT.body,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 16,
    color: C.text3,
    cursor: "pointer",
    padding: "2px 6px",
    lineHeight: 1,
    fontFamily: FONT.body,
  },
  errorBox: {
    margin: "0 24px 12px",
    padding: "10px 14px",
    backgroundColor: "var(--color-danger-soft)",
    border: "1px solid rgba(218,41,28,0.25)",
    borderRadius: 8,
    fontSize: 13,
    color: C.red,
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontFamily: FONT.body,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: "0 24px",
    marginBottom: 14,
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    color: C.text2,
    textTransform: "uppercase" as const,
    letterSpacing: "0.8px",
    fontFamily: FONT.body,
  },
  input: {
    padding: "11px 14px",
    border: `1px solid ${C.border2}`,
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    color: C.text,
    backgroundColor: C.surface2,
    fontFamily: FONT.body,
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  inputFocus: {
    border: `1px solid ${C.red}`,
    boxShadow: "0 0 0 3px var(--color-danger-soft)",
  },
  loginSubmitBtn: {
    width: "100%",
    padding: 13,
    backgroundColor: C.red,
    color: C.onAccent,
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    letterSpacing: "0.3px",
  },
  modalFooter: {
    textAlign: "center" as const,
    fontSize: 11,
    color: C.text3,
    padding: "12px 24px 20px",
    fontFamily: FONT.body,
  },
  spinner: {
    width: 14,
    height: 14,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    display: "inline-block",
  },

  // ── Video modal
  videoModal: {
    backgroundColor: "#000",
    borderRadius: 14,
    overflow: "hidden",
    width: "100%",
    maxWidth: 900,
    boxShadow: "0 40px 100px rgba(0,0,0,0.8)",
    border: `1px solid ${C.border}`,
  },
  videoModalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 18px",
    backgroundColor: "#0d0d0d",
    borderBottom: `1px solid ${C.border}`,
  },
  videoModalTitle: {
    color: C.text,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.5px",
    fontFamily: FONT.body,
  },
  videoEl: {
    width: "100%",
    display: "block",
    maxHeight: "80vh",
    objectFit: "contain",
    backgroundColor: "#000",
  },

  // ── Cookie bar
  cookieBar: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: C.surface,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 24px",
    zIndex: 300,
    gap: 16,
    flexWrap: "wrap" as const,
    boxShadow: "0 -4px 24px rgba(0,0,0,0.3)",
    borderTop: `1px solid ${C.border}`,
  },
  cookieText: {
    fontSize: 13,
    color: C.text,
    lineHeight: 1.4,
    fontFamily: FONT.body,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap" as const,
    gap: 8,
  },
  cookieLearnBtn: {
    background: "none",
    border: "none",
    color: C.red,
    fontSize: 13,
    cursor: "pointer",
    padding: 0,
    fontFamily: FONT.body,
    textDecoration: "underline",
  },
  cookieAcceptBtn: {
    padding: "9px 22px",
    backgroundColor: C.red,
    color: C.onAccent,
    border: "none",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
    whiteSpace: "nowrap" as const,
  },
};
